
import { useEffect, useRef, useState, type KeyboardEvent } from "react";
import { Eraser, Play, Plus, RotateCcw, Sparkles, Trash2 } from "lucide-react";
import type { WorkbookOverlay } from "@/lib/mapper/types";
import { AI_RULE_PLACEHOLDER } from "@/lib/mapper/mapping";
import { baseRowId } from "./_hooks/useOverlay";
import { CellHoverCard } from "./CellHoverCard";
import { FullValueModal } from "./FullValueModal";

export type GridColumn = {
  name: string;
  moduleKind?:
    | "template"
    | "ai"
    | "source"
    | "computed"
    | "dictionary"
    | "expression"
    | "conditional"
    | "static"
    | "manual";
};

export type GridRow = {
  rowId: string;
  cells: Record<string, string>;
};

export type GridCellEdit = {
  rowId: string;
  column: string;
  value: string;
};

type SetOverride = (rowId: string, column: string, value: string) => void;
type ResetCell = (rowId: string, column: string) => void;
type AddRow = () => void;
type AddColumn = (name: string) => void;
type RemoveRows = (rowIds: string[]) => void;

type FullValueState = {
  column: string;
  value: string;
};

type Props = {
  columns: GridColumn[];
  rows: GridRow[];
  overlay: WorkbookOverlay;
  setOverride: SetOverride;
  resetCell: ResetCell;
  selectedColumn?: string | null;
  loading?: boolean;
  playingColumn?: string | null;
  onSelectColumn: (column: string) => void;
  onGenerate?: (column: string) => void;
  /** Forget a cell's stored AI output (it goes back to "pending"). */
  onClearGenerated?: (rowId: string, column: string) => void;
  onResetColumn?: (column: string) => void;
  onAddRow?: AddRow;
  onAddColumn?: AddColumn;
  onRemoveRows?: RemoveRows;
};

export function getGridCellState(
  row: GridRow,
  overlay: WorkbookOverlay,
  column: string,
  isAIColumn = false,
): { value: string; overridden: boolean; generated: boolean } {
  const rowOverrides = overlay.overrides[row.rowId] ?? {};
  const overridden = Object.prototype.hasOwnProperty.call(rowOverrides, column);
  const value = overridden
    ? (rowOverrides[column] ?? "")
    : (row.cells[column] ?? "");
  // AI output stored in the workbook (kept across other generations/reloads).
  const generated =
    isAIColumn &&
    !overridden &&
    value !== "" &&
    overlay.generated?.[baseRowId(row.rowId)]?.[column] === value;
  return { value, overridden, generated };
}

export function commitGridCellEdit(
  edit: GridCellEdit,
  setOverride: SetOverride,
) {
  setOverride(edit.rowId, edit.column, edit.value);
}

export function resetGridCellOverride(
  rowId: string,
  column: string,
  resetCell: ResetCell,
) {
  resetCell(rowId, column);
}

export function toggleRowSelection(
  selectedRowIds: ReadonlySet<string>,
  rowId: string,
  checked?: boolean,
): Set<string> {
  const next = new Set(selectedRowIds);
  const shouldSelect = checked ?? !next.has(rowId);
  if (shouldSelect) next.add(rowId);
  else next.delete(rowId);
  return next;
}

export function toggleAllRows(
  rows: readonly GridRow[],
  checked: boolean,
): Set<string> {
  return checked ? new Set(rows.map((row) => row.rowId)) : new Set();
}

export function selectedRowIdsInOrder(
  rows: readonly GridRow[],
  selectedRowIds: ReadonlySet<string>,
): string[] {
  return rows
    .filter((row) => selectedRowIds.has(row.rowId))
    .map((row) => row.rowId);
}

export function getRowSelectionState(
  rows: readonly GridRow[],
  selectedRowIds: ReadonlySet<string>,
): {
  selectedIds: string[];
  allSelected: boolean;
  someSelected: boolean;
  indeterminate: boolean;
} {
  const selectedIds = selectedRowIdsInOrder(rows, selectedRowIds);
  const someSelected = selectedIds.length > 0;
  const allSelected = rows.length > 0 && selectedIds.length === rows.length;
  return {
    selectedIds,
    allSelected,
    someSelected,
    indeterminate: someSelected && !allSelected,
  };
}

export function requestBulkRowDelete(
  rows: readonly GridRow[],
  selectedRowIds: ReadonlySet<string>,
  removeRows: RemoveRows,
): string[] {
  const selectedIds = selectedRowIdsInOrder(rows, selectedRowIds);
  if (selectedIds.length > 0) {
    removeRows(selectedIds);
  }
  return selectedIds;
}

export function WorkbookGrid({
  columns,
  rows,
  overlay,
  setOverride,
  resetCell,
  selectedColumn,
  loading = false,
  playingColumn = null,
  onSelectColumn,
  onGenerate,
  onClearGenerated,
  onResetColumn,
  onAddRow,
  onAddColumn,
  onRemoveRows,
}: Props) {
  const [full, setFull] = useState<FullValueState | null>(null);
  const [editing, setEditing] = useState<
    { rowId: string; column: string; value: string; original: string } | null
  >(null);
  const [selectedCell, setSelectedCell] = useState<{
    rowId: string;
    column: string;
  } | null>(null);
  const [selectedRowIds, setSelectedRowIds] = useState<Set<string>>(
    () => new Set(),
  );
  const cancelledEditRef = useRef<string | null>(null);
  const bodyRows = rows.length > 0 ? rows : [{ rowId: "__empty__", cells: {} }];
  const selectionState = getRowSelectionState(rows, selectedRowIds);
  const selectedCount = selectionState.selectedIds.length;

  useEffect(() => {
    setSelectedRowIds((current) => {
      const visible = new Set(rows.map((row) => row.rowId));
      let changed = false;
      const next = new Set<string>();
      for (const rowId of current) {
        if (visible.has(rowId)) next.add(rowId);
        else changed = true;
      }
      return changed ? next : current;
    });
  }, [rows]);

  function cellKey(rowId: string, column: string) {
    return `${rowId}\u0000${column}`;
  }

  function selectCell(rowId: string, column: string) {
    if (rowId === "__empty__") {
      onSelectColumn(column);
      return;
    }
    setSelectedCell({ rowId, column });
    onSelectColumn(column);
  }

  function beginEdit(rowId: string, column: string, value: string) {
    if (rowId === "__empty__") {
      onSelectColumn(column);
      return;
    }
    cancelledEditRef.current = null;
    setSelectedCell({ rowId, column });
    setEditing({ rowId, column, value, original: value });
    onSelectColumn(column);
  }

  function updateEditValue(rowId: string, column: string, value: string) {
    setEditing((current) =>
      current?.rowId === rowId && current.column === column
        ? { ...current, value }
        : current,
    );
  }

  function commitEdit(rowId: string, column: string) {
    const key = cellKey(rowId, column);
    if (cancelledEditRef.current === key) {
      cancelledEditRef.current = null;
      return;
    }
    if (editing?.rowId !== rowId || editing.column !== column) {
      return;
    }
    // Only persist an override when the value actually changed. Merely clicking
    // (or focusing) a cell and leaving must NOT mark it as edited.
    if (editing.value !== editing.original) {
      commitGridCellEdit(
        { rowId, column, value: editing.value },
        setOverride,
      );
    }
    setEditing(null);
  }

  function cancelEdit(rowId: string, column: string) {
    cancelledEditRef.current = cellKey(rowId, column);
    setEditing((current) =>
      current?.rowId === rowId && current.column === column ? null : current,
    );
  }

  function promptForColumn() {
    if (!onAddColumn) return;
    const name = window.prompt("Field name");
    if (name) onAddColumn(name);
  }

  function handleBulkDelete() {
    if (!onRemoveRows) return;
    requestBulkRowDelete(rows, selectedRowIds, onRemoveRows);
    setSelectedRowIds(new Set());
  }

  return (
    <div className="relative flex h-full min-h-0 flex-col bg-white dark:bg-surface">
      {selectedCount > 0 && (
        <div className="flex shrink-0 items-center gap-3 border-b border-slate-200 bg-slate-50 px-3 py-2 text-[12px] dark:border-border dark:bg-surface-2">
          <span className="font-medium text-slate-700 dark:text-slate-200">
            {selectedCount} selected
          </span>
          <span className="text-slate-300 dark:text-slate-600">·</span>
          <button
            type="button"
            onClick={handleBulkDelete}
            className="inline-flex items-center gap-1 rounded border border-red-200 bg-white px-2 py-1 text-[12px] font-medium text-red-700 transition hover:bg-red-50 dark:border-red-900/70 dark:bg-surface dark:text-red-300 dark:hover:bg-red-950/40"
          >
            <Trash2 className="h-3 w-3" />
            Delete
          </button>
        </div>
      )}

      <div className="min-h-0 flex-1 overflow-auto">
        <table className="border-separate border-spacing-0 text-[13px]">
          <thead>
            <tr>
              <th className="sticky left-0 top-0 z-30 w-12 border-b border-r border-slate-200 bg-slate-100 px-2 py-2.5 text-center dark:border-border dark:bg-surface-2">
                <SelectAllCheckbox
                  checked={selectionState.allSelected}
                  indeterminate={selectionState.indeterminate}
                  disabled={rows.length === 0}
                  onChange={(checked) =>
                    setSelectedRowIds(toggleAllRows(rows, checked))
                  }
                />
              </th>
            {columns.map((column) => (
              <HeaderCell
                key={column.name}
                column={column}
                selected={column.name === selectedColumn}
                playing={playingColumn === column.name}
                hasPending={rows.some(
                  (row) => row.cells[column.name] === AI_RULE_PLACEHOLDER,
                )}
                onSelect={() => onSelectColumn(column.name)}
                onGenerate={
                  onGenerate && column.moduleKind === "ai"
                    ? () => onGenerate(column.name)
                    : undefined
                }
                onReset={
                  onResetColumn &&
                  column.moduleKind &&
                  column.moduleKind !== "manual"
                    ? () => onResetColumn(column.name)
                    : undefined
                  }
              />
            ))}
            <th className="sticky top-0 z-20 min-w-[116px] border-b border-r border-slate-200 bg-slate-100 px-2 py-2 text-left dark:border-border dark:bg-surface-2">
              <button
                type="button"
                onClick={promptForColumn}
                disabled={!onAddColumn}
                className="inline-flex items-center gap-1 rounded border border-slate-300 bg-white px-2 py-1 text-[11px] font-medium text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-border dark:bg-surface dark:text-slate-300 dark:hover:bg-surface-2"
              >
                <Plus className="h-3 w-3" />
                field
              </button>
            </th>
            </tr>
          </thead>
          <tbody>
            {bodyRows.map((row, rowIndex) => (
              <tr key={`${row.rowId}:${rowIndex}`} className="group/row">
                <RowNumberCell
                  rowNumber={rows.length > 0 ? rowIndex + 1 : null}
                  rowId={row.rowId}
                  selectable={rows.length > 0}
                  checked={selectedRowIds.has(row.rowId)}
                  selectionActive={selectedCount > 0}
                  onChange={(checked) =>
                    setSelectedRowIds((current) =>
                      toggleRowSelection(current, row.rowId, checked),
                    )
                  }
                />
              {columns.map((column) => {
                const { value, overridden, generated } = getGridCellState(
                  row,
                  overlay,
                  column.name,
                  column.moduleKind === "ai",
                );
                const isEditing =
                  editing?.rowId === row.rowId &&
                  editing.column === column.name;
                return (
                  <GridCell
                    key={column.name}
                    rowId={row.rowId}
                    column={column.name}
                    value={value}
                    editValue={isEditing ? editing.value : null}
                    isSelected={column.name === selectedColumn}
                    isCellSelected={
                      selectedCell?.rowId === row.rowId &&
                      selectedCell.column === column.name
                    }
                    isAIPending={value === AI_RULE_PLACEHOLDER}
                    overridden={overridden}
                    generated={generated}
                    onClearGenerated={
                      onClearGenerated
                        ? () => onClearGenerated(row.rowId, column.name)
                        : undefined
                    }
                    editable={rows.length > 0}
                    onSelectColumn={() => onSelectColumn(column.name)}
                    onSelectCell={() => selectCell(row.rowId, column.name)}
                    onStartEdit={() => beginEdit(row.rowId, column.name, value)}
                    onChangeEdit={(nextValue) =>
                      updateEditValue(row.rowId, column.name, nextValue)
                    }
                    onCommitEdit={() => commitEdit(row.rowId, column.name)}
                    onCancelEdit={() => cancelEdit(row.rowId, column.name)}
                    onResetOverride={() =>
                      resetGridCellOverride(row.rowId, column.name, resetCell)
                    }
                    onViewFull={(cellColumn, cellValue) =>
                      setFull({ column: cellColumn, value: cellValue })
                    }
                  />
                );
              })}
              <td className="min-w-[116px] border-b border-r border-slate-100 bg-white dark:border-border dark:bg-surface" />
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex shrink-0 items-center border-t border-slate-200 bg-white px-3 py-2 dark:border-border dark:bg-surface">
        <button
          type="button"
          onClick={onAddRow}
          disabled={!onAddRow}
          className="inline-flex items-center gap-1 rounded border border-slate-300 bg-white px-2.5 py-1.5 text-[12px] font-medium text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-border dark:bg-surface dark:text-slate-300 dark:hover:bg-surface-2"
        >
          <Plus className="h-3 w-3" />
          add product
        </button>
      </div>
      {loading && (
        <div className="pointer-events-none absolute right-3 top-2 rounded-full bg-slate-900/80 px-2 py-0.5 text-[10.5px] font-medium text-white">
          Updating…
        </div>
      )}
      {full && (
        <FullValueModal
          column={full.column}
          value={full.value}
          onClose={() => setFull(null)}
        />
      )}
    </div>
  );
}

function SelectAllCheckbox({
  checked,
  indeterminate,
  disabled,
  onChange,
}: {
  checked: boolean;
  indeterminate: boolean;
  disabled: boolean;
  onChange: (checked: boolean) => void;
}) {
  const ref = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (ref.current) {
      ref.current.indeterminate = indeterminate;
    }
  }, [indeterminate]);

  return (
    <input
      ref={ref}
      type="checkbox"
      aria-label="Select all rows"
      checked={checked}
      disabled={disabled}
      onChange={(event) => onChange(event.currentTarget.checked)}
      className="h-3.5 w-3.5 rounded border-slate-300 text-slate-900 disabled:cursor-not-allowed disabled:opacity-40 dark:border-slate-600"
    />
  );
}

function RowNumberCell({
  rowNumber,
  rowId,
  selectable,
  checked,
  selectionActive,
  onChange,
}: {
  rowNumber: number | null;
  rowId: string;
  selectable: boolean;
  checked: boolean;
  selectionActive: boolean;
  onChange: (checked: boolean) => void;
}) {
  return (
    <td className="sticky left-0 z-10 border-b border-r border-slate-200 bg-white px-2 py-2 text-center text-[10px] text-slate-400 group-hover/row:bg-slate-50 dark:border-border dark:bg-surface dark:text-slate-500 dark:group-hover/row:bg-surface-2">
      {selectable ? (
        <label className="relative inline-flex h-4 w-8 items-center justify-center">
          <span
            aria-hidden={selectionActive || checked}
            className={[
              "font-medium tabular-nums transition",
              selectionActive || checked
                ? "opacity-0"
                : "opacity-100 group-hover/row:opacity-0",
            ].join(" ")}
          >
            {rowNumber}
          </span>
          <input
            type="checkbox"
            aria-label={`Select row ${rowNumber}`}
            checked={checked}
            onChange={(event) => onChange(event.currentTarget.checked)}
            onClick={(event) => event.stopPropagation()}
            className={[
              "absolute left-1/2 top-1/2 h-3.5 w-3.5 -translate-x-1/2 -translate-y-1/2 rounded border-slate-300 text-slate-900 transition dark:border-slate-600",
              selectionActive || checked
                ? "opacity-100"
                : "opacity-0 group-hover/row:opacity-100 focus:opacity-100",
            ].join(" ")}
            data-row-id={rowId}
          />
        </label>
      ) : null}
    </td>
  );
}

function HeaderCell({
  column,
  selected,
  playing,
  hasPending,
  onSelect,
  onGenerate,
  onReset,
}: {
  column: GridColumn;
  selected: boolean;
  playing: boolean;
  /** Some visible cell of this column still waits for AI output. */
  hasPending: boolean;
  onSelect: () => void;
  onGenerate?: () => void;
  onReset?: () => void;
}) {
  return (
    <th
      onClick={onSelect}
      title={`${column.name}, click to edit`}
      className={[
        "group/h sticky top-0 z-20 min-w-[170px] max-w-[300px] cursor-pointer border-b border-r px-3 py-2 text-left align-middle transition",
        selected
          ? "border-slate-900 bg-slate-900 text-white dark:border-slate-100 dark:bg-slate-100 dark:text-slate-950"
          : "border-slate-200 bg-slate-100 text-slate-700 hover:bg-slate-200 dark:border-border dark:bg-surface-2 dark:text-slate-300 dark:hover:bg-surface-2",
      ].join(" ")}
    >
      <div className="flex min-w-0 items-start gap-2">
        <KindMark kind={column.moduleKind} selected={selected} />
        <div className="min-w-0 flex-1">
          <span
            className="block truncate font-mono text-[11.5px] font-semibold"
            title={column.name}
          >
            {column.name}
          </span>
          <ModuleBadge kind={column.moduleKind} selected={selected} />
        </div>
        <div className="flex shrink-0 items-center gap-0.5">
          {onGenerate && (
            <button
              type="button"
              onClick={(event) => {
                event.stopPropagation();
                onGenerate();
              }}
              disabled={playing}
              title={
                hasPending
                  ? "Generate this AI column on the visible rows (cells that already have AI output keep it)"
                  : "Regenerate this AI column on the visible rows (replaces the current AI output)"
              }
              aria-label={`${hasPending ? "Generate" : "Regenerate"} ${column.name}`}
              className={[
                "rounded p-1 transition",
                selected
                  ? "text-white/80 hover:bg-white/15"
                  : "text-slate-400 hover:bg-white hover:text-emerald-600 dark:text-slate-500",
              ].join(" ")}
            >
              {playing ? (
                <Spinner />
              ) : hasPending ? (
                <Play className="h-3 w-3 fill-current" />
              ) : (
                <Sparkles className="h-3 w-3" />
              )}
            </button>
          )}
          {onReset && (
            <button
              type="button"
              onClick={(event) => {
                event.stopPropagation();
                onReset();
              }}
              title="Reset column rule"
              aria-label={`Reset ${column.name}`}
              className={[
                "rounded p-1 opacity-0 transition group-hover/h:opacity-100 focus:opacity-100",
                selected
                  ? "text-white/70 hover:bg-white/15"
                  : "text-slate-400 hover:bg-white hover:text-rose-600 dark:text-slate-500",
              ].join(" ")}
            >
              <RotateCcw className="h-3 w-3" />
            </button>
          )}
        </div>
      </div>
    </th>
  );
}

function GridCell({
  rowId,
  column,
  value,
  editValue,
  isSelected,
  isCellSelected,
  isAIPending,
  overridden,
  generated = false,
  onClearGenerated,
  editable,
  onSelectColumn,
  onSelectCell,
  onStartEdit,
  onChangeEdit,
  onCommitEdit,
  onCancelEdit,
  onResetOverride,
  onViewFull,
}: {
  rowId: string;
  column: string;
  value: string;
  editValue: string | null;
  isSelected: boolean;
  isCellSelected: boolean;
  isAIPending: boolean;
  overridden: boolean;
  generated?: boolean;
  onClearGenerated?: () => void;
  editable: boolean;
  onSelectColumn: () => void;
  onSelectCell: () => void;
  onStartEdit: () => void;
  onChangeEdit: (value: string) => void;
  onCommitEdit: () => void;
  onCancelEdit: () => void;
  onResetOverride: () => void;
  onViewFull: (column: string, value: string) => void;
}) {
  const [hovered, setHovered] = useState(false);
  const editing = editValue !== null;
  const showHoverCard =
    hovered && value !== "" && !isAIPending && !editing && !isCellSelected;

  // Single click = select the whole cell (no edit, no override). Double click =
  // edit. This makes "clicking ≠ editing" and highlights the whole cell.
  function handleClick() {
    if (editing) return;
    if (editable) {
      onSelectCell();
    } else {
      onSelectColumn();
    }
  }

  function handleDoubleClick() {
    if (editing) return;
    if (editable) onStartEdit();
  }

  function handleKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === "Enter") {
      event.preventDefault();
      onCommitEdit();
    } else if (event.key === "Escape") {
      event.preventDefault();
      onCancelEdit();
    }
  }

  const highlighted = isCellSelected || editing;

  return (
    <td
      onClick={handleClick}
      onDoubleClick={handleDoubleClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      className={[
        "group/cell relative max-w-[320px] min-w-[160px] whitespace-nowrap border-b border-r border-slate-100 px-3 py-2 align-middle dark:border-border",
        editable ? "cursor-pointer" : "cursor-pointer",
        overridden || generated ? "pr-8" : "",
        // Whole-cell selection ring (box-shadow, no layout shift) — the cell
        // border itself highlights, not an inner box.
        highlighted ? "z-10 ring-2 ring-inset ring-sky-500 dark:ring-sky-400" : "",
        isSelected && !highlighted
          ? "bg-sky-50/70 dark:bg-sky-950/40"
          : highlighted
            ? "bg-white dark:bg-surface"
            : "bg-white group-hover:bg-slate-50 dark:bg-surface dark:group-hover:bg-surface-2",
      ].join(" ")}
      title={isAIPending ? "AI field, click ▶ in the header to generate" : value}
    >
      {editing ? (
        <input
          autoFocus
          aria-label={`Edit ${column} for row ${rowId}`}
          value={editValue}
          onClick={(event) => event.stopPropagation()}
          onChange={(event) => onChangeEdit(event.currentTarget.value)}
          onKeyDown={handleKeyDown}
          onBlur={onCommitEdit}
          onFocus={(event) => event.currentTarget.select()}
          className="absolute inset-0 h-full w-full min-w-0 bg-white px-3 py-2 text-[13px] text-slate-900 outline-none dark:bg-surface dark:text-slate-100"
        />
      ) : isAIPending ? (
        <span className="inline-flex items-center gap-1 rounded bg-violet-50 px-1.5 py-0.5 text-[11px] font-medium text-violet-700 ring-1 ring-violet-200 dark:bg-violet-950/40 dark:text-violet-300 dark:ring-violet-800">
          <Play className="h-2.5 w-2.5 fill-current" /> generate
        </span>
      ) : value === "" ? (
        <span className="text-slate-300 dark:text-slate-600">Blank</span>
      ) : (
        <span
          className={[
            "inline-flex max-w-full items-center gap-1.5 rounded-full px-2 py-0.5 ring-1",
            overridden
              ? "bg-amber-50 text-amber-900 ring-amber-200 dark:bg-amber-950/40 dark:text-amber-200 dark:ring-amber-800"
              : generated
                ? "bg-violet-50 text-violet-950 ring-violet-200 dark:bg-violet-950/40 dark:text-violet-100 dark:ring-violet-800"
                : "bg-slate-50 text-slate-800 ring-slate-200 dark:bg-surface-2 dark:text-slate-200 dark:ring-slate-700",
          ].join(" ")}
        >
          <span
            className={[
              "shrink-0 text-[9px] font-semibold uppercase tracking-wide",
              overridden
                ? "text-amber-600 dark:text-amber-300"
                : generated
                  ? "text-violet-600 dark:text-violet-300"
                  : "text-slate-400 dark:text-slate-500",
            ].join(" ")}
            title={
              generated
                ? "AI output saved in this workbook. It is kept when other columns are generated and is what the export uses."
                : undefined
            }
          >
            {overridden ? "manual" : generated ? "AI" : "auto"}
          </span>
          <span className="truncate">{value}</span>
        </span>
      )}
      {generated && !editing && onClearGenerated && (
        <button
          type="button"
          aria-label={`Clear AI output for ${column}`}
          title="Clear this AI output (the cell goes back to pending; ▶ generates it again)"
          onMouseDown={(event) => event.stopPropagation()}
          onClick={(event) => {
            event.stopPropagation();
            onClearGenerated();
          }}
          className="absolute right-1 top-1/2 -translate-y-1/2 rounded p-0.5 text-slate-400 opacity-0 transition hover:bg-slate-100 hover:text-rose-600 group-hover/cell:opacity-100 focus:opacity-100 dark:text-slate-500 dark:hover:bg-surface-2 dark:hover:text-rose-300"
        >
          <Eraser className="h-3 w-3" />
        </button>
      )}
      {overridden && !editing && (
        <span
          aria-label={`Manual override for ${column}`}
          title="Manual override"
          className="absolute right-1.5 top-1 text-[10px] font-semibold leading-none text-amber-600 dark:text-amber-300"
        >
          ✎
        </span>
      )}
      {overridden && !editing && (
        <button
          type="button"
          aria-label={`Reset override for ${column}`}
          title="Reset to module value"
          onMouseDown={(event) => event.stopPropagation()}
          onClick={(event) => {
            event.stopPropagation();
            onResetOverride();
          }}
          className="absolute bottom-1 right-1 rounded p-0.5 text-slate-400 opacity-0 transition hover:bg-slate-100 hover:text-rose-600 group-hover/cell:opacity-100 focus:opacity-100 dark:text-slate-500 dark:hover:bg-surface-2 dark:hover:text-rose-300"
        >
          <RotateCcw className="h-3 w-3" />
        </button>
      )}
      {showHoverCard && (
        <CellHoverCard
          value={value}
          onViewFull={() => {
            onViewFull(column, value);
            setHovered(false);
          }}
        />
      )}
    </td>
  );
}

function ModuleBadge({
  kind,
  selected,
}: {
  kind: GridColumn["moduleKind"];
  selected: boolean;
}) {
  const label = badgeLabel(kind);
  return (
    <span
      className={[
        "mt-1 inline-flex max-w-full items-center rounded px-1.5 py-0.5 text-[10px] font-medium leading-none ring-1",
        selected
          ? "bg-white/10 text-white/85 ring-white/20 dark:bg-slate-950/10 dark:text-slate-800 dark:ring-slate-900/15"
          : badgeTone(kind),
      ].join(" ")}
      title={`Module kind: ${label}`}
    >
      {label}
    </span>
  );
}

function KindMark({
  kind,
  selected,
}: {
  kind: GridColumn["moduleKind"];
  selected: boolean;
}) {
  return (
    <span
      aria-hidden
      className={[
        "mt-1.5 inline-block h-2 w-2 shrink-0 rounded-full ring-1",
        selected ? "ring-white/40" : "ring-slate-300 dark:ring-slate-700",
        markTone(kind),
      ].join(" ")}
    />
  );
}

function badgeLabel(kind: GridColumn["moduleKind"]): string {
  switch (kind) {
    case "template":
      return "ƒtemplate";
    case "ai":
      return "ƒAI";
    case "computed":
      return "ƒcomputed";
    case "dictionary":
      return "ƒdictionary";
    case "expression":
      return "ƒexpr";
    case "conditional":
      return "ƒif";
    case "source":
      return "source";
    case "static":
      return "static";
    case "manual":
      return "manual";
    default:
      return "field";
  }
}

function badgeTone(kind: GridColumn["moduleKind"]): string {
  switch (kind) {
    case "ai":
      return "bg-violet-50 text-violet-700 ring-violet-200 dark:bg-violet-950/40 dark:text-violet-300 dark:ring-violet-800";
    case "template":
    case "expression":
      return "bg-sky-50 text-sky-700 ring-sky-200 dark:bg-sky-950/40 dark:text-sky-300 dark:ring-sky-800";
    case "computed":
    case "conditional":
      return "bg-amber-50 text-amber-700 ring-amber-200 dark:bg-amber-950/40 dark:text-amber-300 dark:ring-amber-800";
    case "dictionary":
      return "bg-teal-50 text-teal-700 ring-teal-200 dark:bg-teal-950/40 dark:text-teal-300 dark:ring-teal-800";
    case "manual":
      return "bg-slate-50 text-slate-600 ring-slate-200 dark:bg-surface dark:text-slate-300 dark:ring-slate-700";
    case "source":
    case "static":
    default:
      return "bg-white text-slate-600 ring-slate-200 dark:bg-surface dark:text-slate-300 dark:ring-slate-700";
  }
}

function markTone(kind: GridColumn["moduleKind"]): string {
  switch (kind) {
    case "ai":
      return "bg-violet-500";
    case "template":
    case "expression":
      return "bg-sky-500";
    case "computed":
    case "conditional":
      return "bg-amber-500";
    case "dictionary":
      return "bg-teal-500";
    case "manual":
      return "bg-slate-300 dark:bg-slate-600";
    case "source":
    case "static":
    default:
      return "bg-emerald-500";
  }
}

function Spinner() {
  return (
    <span
      aria-hidden
      className="inline-block h-3 w-3 animate-spin rounded-full border-2 border-emerald-300 border-t-transparent"
    />
  );
}
